// chatbot.component.ts
import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ChatResponse } from 'src/app/core/interfaces/message.interface';
import { ChatService } from '../../../services/chatbot.service';
import { Subject, takeUntil } from 'rxjs';

interface Message {
  id: string;
 type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  analysis?: MessageAnalysis;
  confidence?: number;
  intent?: string;
  response?: string[]
  columns?: string[];
}

export interface MessageAnalysis {
  intent: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  confidence: number;
  entities: string[];
  topics: string[];
}

@Component({
  selector: 'app-chatbot',

 templateUrl: './chat-bot.component.html',
  styleUrls: ['./chat-bot.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('in', style({
        transform: 'translateY(0)',
        opacity: 1
      })),
      state('out', style({
        transform: 'translateY(100%)',
        opacity: 0
      })),
      transition('out => in', animate('300ms ease-out')),
      transition('in => out', animate('300ms ease-in'))
    ]),
    trigger('bounceIn', [
      transition(':enter', [
        style({ transform: 'scale(0.3)', opacity: 0 }),
        animate('300ms cubic-bezier(0.68, -0.55, 0.265, 1.55)', 
          style({ transform: 'scale(1)', opacity: 1 }))
      ])
    ])
  ],

  styles: [`
    .chatbot-toggle {
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 50px;
      padding: 15px 20px;
      cursor: pointer;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      display: flex;
      align-items: center;
      gap: 10px;
      transition: all 0.3s ease;
      z-index: 1000;
    }

    .chatbot-toggle:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 25px rgba(0,0,0,0.2);
    }

    .chatbot-toggle.expanded {
      border-radius: 50%;
      padding: 15px;
      background: #f44336;
    }

    .toggle-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .toggle-text {
      font-weight: 500;
      white-space: nowrap;
    }

    .chatbot-container {
      position: fixed;
      bottom: 90px;
      right: 20px;
      width: 380px;
      height: 500px;
      background: white;
      border-radius: 16px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.1);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      z-index: 999;
      border: 1px solid #e0e0e0;
    }

    .chatbot-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 16px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .header-content {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .bot-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: rgba(255,255,255,0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .header-info h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
    }

    .status {
      margin: 0;
      font-size: 12px;
      opacity: 0.9;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .status-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #4caf50;
      display: inline-block;
    }

    .minimize-btn {
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      opacity: 0.8;
      transition: opacity 0.2s;
    }

    .minimize-btn:hover {
      opacity: 1;
      background: rgba(255,255,255,0.1);
    }

    .messages-container {
      flex: 1;
      overflow-y: auto;
      padding: 0;
      background: #f8f9fa;
    }

    .messages-list {
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .message {
      display: flex;
      gap: 10px;
      margin-bottom: 8px;
    }

    .user-message {
      justify-content: flex-end;
    }

    .user-message .message-content {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 18px 18px 4px 18px;
    }

    .bot-message .message-content {
      background: white;
      border: 1px solid #e1e5e9;
      border-radius: 18px 18px 18px 4px;
      color: #2c3e50;
    }

    .message-content {
      max-width: 95%;
      hight: auto;
      font-size: 14px;
      line-height: 1.4;
      padding: 12px 16px;
      word-wrap: break-word;
    }

    .message-content p {
      margin: 0 0 4px 0;
      line-height: 1.4;
    }

    .message-content ul {
      margin: 8px 0;
      padding-left: 20px;
    }

    .message-content li {
      margin-bottom: 4px;
    }

    .message-time {
      font-size: 10px;
      opacity: 0.6;
      margin-top: 4px;
      display: block;
    }

    .message-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      flex-shrink: 0;
      margin-top: 4px;
    }

    .welcome-message {
      margin-bottom: 16px;
    }

    .loading-message {
      opacity: 0.7;
    }

    .typing-indicator {
      display: flex;
      gap: 3px;
      padding: 8px 0;
    }

    .typing-indicator span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #999;
      animation: typing 1.4s infinite ease-in-out;
    }

    .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
    .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

    @keyframes typing {
      0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
      40% { transform: scale(1); opacity: 1; }
    }

    .input-container {
      padding: 16px 20px;
      background: white;
      border-top: 1px solid #e1e5e9;
    }

    .input-wrapper {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .message-input {
      flex: 1;
      padding: 14px 18px;
      border: 2px solid lighten($border-color, 5%);
      @include border-radius(20px);
      resize: none;
      outline: none;
      font-family: inherit;
      font-size: 14px;
      line-height: 1.5;
      max-height: 120px;
      min-height: 48px;
      @include transition(all, 0.3s);
      background: $light-color;

      &::placeholder {
        color: $secondary-color;
        opacity: 0.7;
      }

      &:focus {
        border-color: $primary-color;
        @include box-shadow(0 0 0 3px rgba($primary-color, 0.15));
        background: $white;
      }

      &:disabled {
        background: lighten($border-color, 10%);
        cursor: not-allowed;
        opacity: 0.7;
      }
    }

    .send-button {
      padding: 12px 24px;
      background: $gradient-message-user;
      color: $white;
      border: none;
      @include border-radius(20px);
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      @include transition(all, 0.3s);
      min-width: 50px;
      height: 44px;
      @include flex-center;
      @include box-shadow(0 2px 4px rgba($primary-color, 0.2));
      
      @include hover-lift;

      &:active {
        transform: translateY(0) scale(0.98);
      }

      &:disabled {
        background: $secondary-color;
        cursor: not-allowed;
        transform: none;
        @include box-shadow(none);
        opacity: 0.6;
      }

      .loading-spinner {
        display: inline-block;
        animation: spin 1s linear infinite;
        font-size: 16px;
      }
    }

    @media (max-width: 480px) {
      .chatbot-container {
        width: calc(100vw - 40px);
        height: calc(100vh - 140px);
        bottom: 80px;
        right: 20px;
        left: 20px;
      }

      .chatbot-toggle {
        bottom: 20px;
        right: 20px;
      }
    }
  `]
})
export class ChatbotComponent implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('messageInput') private messageInput!: ElementRef;
  getUserName: string | null = sessionStorage.getItem("username");
  speackerName:string = this.getUserName?.includes(',') ? this.getUserName.substring(this.getUserName.indexOf(',') + 1).trim(): this.getUserName || '';

  isExpanded = false;
  messages: Message[] = [];
  currentMessage = '';
  isLoading = false;
  isConnected = true;
  animationState = 'out';
  


  isTyping = false;
  error: string | null = null;
  
  private destroy$ = new Subject<void>();
  private shouldScrollToBottom = false;

  private readonly apiUrl = 'http://localhost:4000/api/v1/chat';

  constructor(private http: HttpClient,private chatService: ChatService) 
  {
  }

  ngOnInit() {
     this.subscribeToMessages();
    this.subscribeToTypingStatus();
    this.focusInput();
  }
    private subscribeToMessages(): void {
    this.chatService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(messages => {
        this.messages = messages;
        this.shouldScrollToBottom = true;
      });
  }

  private subscribeToTypingStatus(): void {
    this.chatService.isTyping$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isTyping => {
        this.isTyping = isTyping;
        if (isTyping) {
          this.shouldScrollToBottom = true;
        }
      });
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  toggleChatbot() {
    this.isExpanded = !this.isExpanded;
    this.animationState = this.isExpanded ? 'in' : 'out';
  }

 addUserMessage(content: string) {
    const message: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: content,
      timestamp: new Date()
    };
    
    this.messages.push(message);
  }

  sendMessage(): void {
    if (!this.currentMessage.trim() || this.isLoading) {
      return;
    }

    const message = this.currentMessage.trim();
    // this.addUserMessage(message);
    this.currentMessage = '';
    this.isLoading = true;
    this.error = null;

    this.chatService.sendMessage(message)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.chatService.addBotResponse(response);
          
          this.isLoading = false;
          this.focusInput();
        },
        error: (error) => {
          this.error = error.message;
          this.isLoading = false;
          this.focusInput();
          
          // Clear error after 5 seconds
          setTimeout(() => this.error = null, 5000);
        }
      });
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  clearConversation(): void {
    if (confirm('Are you sure you want to clear this conversation?')) {
      this.chatService.clearLocalConversation();
      this.error = null;
      this.focusInput();
    }
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    } catch (error) {
      console.warn('Could not scroll to bottom:', error);
    }
  }

  private focusInput(): void {
    setTimeout(() => {
      if (this.messageInput) {
        this.messageInput.nativeElement.focus();
      }
    }, 100);
  }

  formatTimestamp(date: Date): string {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getSentimentClass(message: Message): string {
    if (!message.analysis?.sentiment) return '';
    
    return `sentiment-${message.analysis.sentiment}`;
  }

  getConfidenceColor(confidence?: number): string {
    if (!confidence) return '#gray';
    
    if (confidence >= 0.8) return '#green';
    if (confidence >= 0.6) return '#yellow';
    return '#red';
  }
}